import { useEffect, useRef } from 'react'
import type { GeoJSONSource, MapMouseEvent, MapTouchEvent } from 'maplibre-gl'
import type { Pos } from '../lib/geo.ts'
import { useMapInstance } from './mapContext.ts'

export type DrawMode = 'polygon' | 'line' | 'point'
interface Props { mode: DrawMode; value: Pos[]; onChange: (next: Pos[]) => void }

const SRC = 'draw'
const LAYERS = ['draw-fill', 'draw-line', 'draw-mid', 'draw-vertex'] as const

function collection(mode: DrawMode, pts: Pos[]) {
  const features: unknown[] = []
  if (mode === 'polygon' && pts.length >= 3) features.push({ type: 'Feature', properties: { part: 'fill' }, geometry: { type: 'Polygon', coordinates: [[...pts, pts[0]]] } })
  if (mode !== 'point' && pts.length >= 2) features.push({ type: 'Feature', properties: { part: 'line' }, geometry: { type: 'LineString', coordinates: mode === 'polygon' && pts.length >= 3 ? [...pts, pts[0]] : pts } })
  if (mode !== 'point') for (let i = 0; i + 1 < pts.length + (mode === 'polygon' && pts.length >= 3 ? 1 : 0); i++) {
    const a = pts[i]!, b = pts[(i + 1) % pts.length]!
    features.push({ type: 'Feature', properties: { part: 'mid', after: i }, geometry: { type: 'Point', coordinates: [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2] } })
  }
  pts.forEach((p, i) => features.push({ type: 'Feature', properties: { part: 'vertex', idx: i, first: i === 0 }, geometry: { type: 'Point', coordinates: p } }))
  return { type: 'FeatureCollection', features }
}

/** Draws on the map the user is looking at: tap to add a point, drag a point to move it, tap a small circle between two points to insert one,
 *  double-tap a point to remove it. The outline is only a request for a new shape; the server validates it and says no if it is wrong. */
export function DrawTool({ mode, value, onChange }: Props) {
  const map = useMapInstance()
  const pts = useRef(value); pts.current = value
  const modeRef = useRef(mode); modeRef.current = mode
  const change = useRef(onChange); change.current = onChange
  // handlers see the newest points at once, not after the next render: two quick taps must make two points
  const emit = (next: Pos[]) => { pts.current = next; change.current(next) }

  useEffect(() => {
    if (!map) return
    map.addSource(SRC, { type: 'geojson', data: collection(modeRef.current, pts.current) as never })
    map.addLayer({ id: 'draw-fill', type: 'fill', source: SRC, filter: ['==', ['get', 'part'], 'fill'], paint: { 'fill-color': '#facc15', 'fill-opacity': 0.3 } })
    map.addLayer({ id: 'draw-line', type: 'line', source: SRC, filter: ['==', ['get', 'part'], 'line'], paint: { 'line-color': '#ca8a04', 'line-width': 3 } })
    map.addLayer({ id: 'draw-mid', type: 'circle', source: SRC, filter: ['==', ['get', 'part'], 'mid'], paint: { 'circle-radius': 6, 'circle-color': '#ffffff', 'circle-opacity': 0.8, 'circle-stroke-color': '#ca8a04', 'circle-stroke-width': 1.5 } })
    map.addLayer({ id: 'draw-vertex', type: 'circle', source: SRC, filter: ['==', ['get', 'part'], 'vertex'],
      paint: { 'circle-radius': ['case', ['get', 'first'], 11, 9], 'circle-color': ['case', ['get', 'first'], '#16a34a', '#ca8a04'], 'circle-stroke-color': '#ffffff', 'circle-stroke-width': 2.5 } })
    map.doubleClickZoom.disable()

    let drag: number | null = null
    let quietUntil = 0
    const move = (lng: number, lat: number) => { if (drag === null) return; const next = pts.current.slice(); next[drag] = [lng, lat]; emit(next) }
    const down = (e: MapMouseEvent | MapTouchEvent) => {
      const f = map.queryRenderedFeatures(e.point, { layers: ['draw-vertex'] })[0]
      if (!f) return
      drag = Number(f.properties?.idx); map.dragPan.disable(); e.preventDefault()
    }
    const onMove = (e: MapMouseEvent | MapTouchEvent) => { if (drag !== null) move(e.lngLat.lng, e.lngLat.lat) }
    const up = () => { if (drag === null) return; drag = null; quietUntil = Date.now() + 300; map.dragPan.enable() }
    const click = (e: MapMouseEvent) => {
      if (Date.now() < quietUntil) return
      if (map.queryRenderedFeatures(e.point, { layers: ['draw-vertex'] }).length) return
      const mid = map.queryRenderedFeatures(e.point, { layers: ['draw-mid'] })[0]
      const here: Pos = [e.lngLat.lng, e.lngLat.lat]
      if (modeRef.current === 'point') { emit([here]); return }
      if (mid) { const next = pts.current.slice(); next.splice(Number(mid.properties?.after) + 1, 0, here); emit(next); return }
      emit([...pts.current, here])
    }
    const dbl = (e: MapMouseEvent) => {
      const f = map.queryRenderedFeatures(e.point, { layers: ['draw-vertex'] })[0]
      if (!f) return
      e.preventDefault()
      const next = pts.current.slice(); next.splice(Number(f.properties?.idx), 1); emit(next)
    }
    map.on('mousedown', 'draw-vertex', down); map.on('touchstart', 'draw-vertex', down)
    map.on('mousemove', onMove); map.on('touchmove', onMove); map.on('mouseup', up); map.on('touchend', up)
    map.on('click', click); map.on('dblclick', dbl)
    return () => {
      try {
      map.off('mousedown', 'draw-vertex', down); map.off('touchstart', 'draw-vertex', down)
      map.off('mousemove', onMove); map.off('touchmove', onMove); map.off('mouseup', up); map.off('touchend', up)
      map.off('click', click); map.off('dblclick', dbl)
      map.dragPan.enable(); map.doubleClickZoom.enable()
      for (const id of LAYERS) if (map.getLayer(id)) map.removeLayer(id)
      if (map.getSource(SRC)) map.removeSource(SRC)
      } catch { /* the map was removed first (a screen closing): nothing left to clean */ }
    }
  }, [map])

  useEffect(() => { (map?.getSource(SRC) as GeoJSONSource | undefined)?.setData(collection(mode, value) as never) }, [map, mode, value])
  return null
}
